/**
 * Synchronous helpers for merging a `ToDoTask` payload into draft Redux state (buckets
 * via `upsertTaskAcrossBuckets` in `todo.util`, then player / task form / search).
 *
 * Separation of concerns: keeps post-merge slice updates out of the large `todo.util`
 * module and separate from async thunk flows (see `todo-brain-dump.util`), which must
 * import `extra` and cannot be folded into `todo.util` without circular imports.
 */
import { ITEM_INDEX_NOT_FOUND } from 'constants/general';
import type { ToDoTask } from 'interfaces/common.interface';
import type { ToDoSlice } from 'interfaces/initial-states.interface';
import {
  isExternalTaskCompleted,
  upsertTaskAcrossBuckets
} from 'utils/todo.util';

export type PlayerAndTaskFormState = Pick<
  ToDoSlice,
  'player' | 'selectedTaskToTaskForm'
>;

export type TodoStateForTaskMerge = Pick<
  ToDoSlice,
  | 'tasks'
  | 'tasksByStatus'
  | 'todoStatusPagination'
  | 'player'
  | 'selectedTaskToTaskForm'
>;

/** Safe guard for API / thunk payloads before bucket + player merge. */
export function isValidTodoTaskMergePayload(value: unknown): value is ToDoTask {
  if (value == null || typeof value !== 'object') return false;
  const task = value as Record<string, unknown>;
  return typeof task.id === 'string' && task.id.length > 0;
}

export function syncPlayerAndTaskFormAfterTaskMerge(
  state: PlayerAndTaskFormState,
  merged: ToDoTask
) {
  const taskId = merged.id;

  const playerTaskIndex = state.player.tasks.findIndex((t) => t.id === taskId);
  if (playerTaskIndex !== -1) {
    state.player.tasks[playerTaskIndex] = {
      ...state.player.tasks[playerTaskIndex],
      ...merged
    };
  }

  if (state.player.selectedTask?.id === taskId) {
    const updatedSelectedTask = { ...state.player.selectedTask, ...merged };
    if (isExternalTaskCompleted(updatedSelectedTask)) {
      state.player.selectedTask = undefined;
      state.player.completedTask = updatedSelectedTask;
      state.player.isSelectedTaskCompleted = true;
    } else {
      state.player.selectedTask = updatedSelectedTask;
    }
  } else if (state.player.completedTask?.id === taskId) {
    const updatedCompletedTask = { ...state.player.completedTask, ...merged };
    if (!isExternalTaskCompleted(updatedCompletedTask)) {
      state.player.completedTask = null;
      state.player.isSelectedTaskCompleted = false;
    } else {
      state.player.completedTask = updatedCompletedTask;
    }
  }

  if (state.selectedTaskToTaskForm?.id === taskId) {
    state.selectedTaskToTaskForm = {
      ...state.selectedTaskToTaskForm,
      ...merged
    };
  }

  if (state.player.searchResults) {
    const searchIx = state.player.searchResults.findIndex(
      (task) => task.id === taskId
    );
    if (searchIx !== ITEM_INDEX_NOT_FOUND) {
      state.player.searchResults[searchIx] = {
        ...state.player.searchResults[searchIx],
        ...merged
      };
    }
  }
}

export function applyMergedTodoTaskFromPayload(
  state: TodoStateForTaskMerge,
  payload: ToDoTask
) {
  const taskIndex = state.tasks.findIndex((task) => task.id === payload.id);
  const prev = taskIndex !== -1 ? state.tasks[taskIndex] : undefined;
  const merged: ToDoTask = prev ? { ...prev, ...payload } : payload;

  upsertTaskAcrossBuckets(state, merged);
  syncPlayerAndTaskFormAfterTaskMerge(state, merged);
}
